/**
 * betUtils.js
 * 
 * Pure helper functions for bet/raise calculations.
 * These functions compute the "action amount" (delta) that gets sent to the backend,
 * NOT the total contribution to the pot.
 * 
 * Backend Invariant: ProcessBetAsync adds `amount` to CurrentRoundStatus and PotSize.
 * Therefore: actionAmount = (currentBet - currentRoundStatus) + raiseByInput
 *   - (currentBet - currentRoundStatus) = chips needed to call
 *   - raiseByInput = additional chips to raise by
 */

/**
 * Computes the action amount (delta) to send to the backend for a raise action.
 * 
 * @param {number} currentBet - The current bet amount to match (table's CurrentBet)
 * @param {number} currentRoundStatus - Player's chips already committed this round
 * @param {number} raiseByInput - The amount the player wants to raise BY (above the call)
 * @returns {number} The action amount (delta) to send to backend
 * 
 * @example
 * // Player needs to call 1 chip (2-1) and raise by 2 more
 * computeRaiseActionAmount(2, 1, 2) // returns 3
 * 
 * @example
 * // Player just calls (raise by 0)
 * computeRaiseActionAmount(2, 1, 0) // returns 1
 * 
 * @example
 * // First bet in round (no prior bet)
 * computeRaiseActionAmount(0, 0, 5) // returns 5
 */
export function computeRaiseActionAmount(currentBet, currentRoundStatus, raiseByInput) {
  const callAmount = currentBet - currentRoundStatus;
  // Handle NaN, undefined, null by coercing to 0
  const safeRaiseBy = Number.isFinite(raiseByInput) ? raiseByInput : 0;
  const clampedRaiseBy = Math.max(0, Math.floor(safeRaiseBy));
  const clampedCall = Math.max(0, callAmount);
  
  return clampedCall + clampedRaiseBy;
}
